<style scoped lang="less">
@import "/lib/Colors.less";
.icon-download:before {
    text-decoration: none;
    margin-right: 5px;
}
.files-list {
    list-style-type: none;
    padding-left: 5px;
}
.visibility-input-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    width: auto;
    height: auto;
    padding: 0;
    font-size: 0.9em;
    .icon {
        &:before {
            margin-right: 5px;
        }
    }
    .btn {
        display: flex;
        align-items: center;
        padding: 0 4px;
    }
}
.icon-sites:before {
    color: @primary-blue-v2;
}
.disabled-visibility {
    cursor: not-allowed !important;
    .visibility-input {
        pointer-events: none;
    }
}
.dropdown-menu {
    right: 0;
    left: auto;
    white-space: nowrap;
    padding: 10px 15px;
    font-size: 0.875rem;
    .icon {
        display: inline-flex;
        align-items: center;
    }
}
.comment-username {
    display: block;
    line-height: 100%;
    margin-bottom: 5px;
}
/deep/ .help-button {
    margin-right: 5px;
    cursor: not-allowed;
}
/deep/ .disabled-help-overlay {
    // overlay help icon on disabled inputs for better reliability on click
    .help-button .icon {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 30px;
        background: none;
        outline: none !important;
        &:before {
            display: none;
        }
    }
    .popper {
        min-width: 200px;
    }
}
</style>
<template>
    <li class="submissions-comment p-relative" :class="!adminReview ? null : comment.visibleToSubmitter ? 'submitter-visible-comment' : 'reviewer-visible-comment'">
        <div v-if="adminReview" class="d-inline-flex ml-auto p-relative float-right">
            <div class="visibility-input-wrapper dropdown" :class="hideHistory ? 'disabled-visibility' : ''" v-if="adminReview && comment.id != '00000000-0000-0000-0000-000000000000'">
                <button v-if="!hideHistory" type="button" :id="id + '_VisibleToggle_' + comment.id" class="existing-visibility btn btn-outline-secondary btn-sm dropdown-toggle icon" :class="{ show: visibleToSubmitterEditing }" @click.prevent.stop="toggleVisibleToSubmitterEditing(comment)" :disabled="hideHistory"><span class="icon" :class="comment.visibleToSubmitter && !hideHistory ? 'icon-sites' : 'icon-users'"></span>{{ comment.visibleToSubmitter ? "Everyone" : "Reviewers only" }}</button>
                <span v-else class="existing-visibility icon icon-users btn btn-bare btn-sm p-0">Reviewers only</span>
                <fieldset class="dropdown-menu" @click.stop>
                    <legend class="sr-only">Comment Visibility</legend>
                    <div class="form-check">
                        <input type="radio" class="form-check-input" :id="id + '_Everyone_' + comment.id" :name="'CommentVisibility_' + comment.id" @change="toggleCommentVisibility(comment, true)" :checked="comment.visibleToSubmitter" />
                        <label class="form-check-label icon" :for="id + '_Everyone_' + comment.id"><span class="icon icon-sites"></span>Everyone</label>
                    </div>
                    <div class="form-check">
                        <input type="radio" class="form-check-input" :id="id + '_Reviewers_' + comment.id" :name="'CommentVisibility_' + comment.id" @change="toggleCommentVisibility(comment, false)" :checked="!comment.visibleToSubmitter" />
                        <label class="form-check-label" :for="id + '_Reviewers_' + comment.id"><span class="icon icon-users"></span>Reviewers only</label>
                    </div>
                </fieldset>
                <HelpIcon v-if="hideHistory" class="disabled-help-overlay" :text="'Comments in this tab are only visible to Reviewers. Comments added for returns will only be included in the notification to the Submitter. Add comments to specific fields to inform the Submitter.'" :hover="false" />
            </div>
        </div>
        <strong class="fw-600 comment-username">{{ comment.userName || "Anonymous" }}</strong>
        <span class="help-block p-0">{{ new Date(comment.date).toLocaleString() }}</span>
        <div class="d-flex justify-content-between align-items-end">
            <div>
                <div class="comment-text">{{ comment.text }}</div>
                <template v-if="comment.files != null && comment.files.length > 0">
                    <strong class="sr-only">Attached Files:</strong>
                    <ul class="files-list">
                        <li v-for="file in comment.files">
                            <a class="icon icon-download" target="_blank" :href="file.fileUrl">{{ file.filename }}</a>
                        </li>
                    </ul>
                </template>
            </div>
            <button v-if="canDeleteComments" class="passthrough icon icon-trash" title="Delete" aria-label="Delete" @click.prevent="$emit('delete-comment')" />
        </div>
    </li>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext } from "vue";
import HelpIcon from "@/lib/HelpIcon.3.vue";
import { useMainStore } from "@/mainStore";
import { usePageStore } from "../pageState/store";
import { FormComment } from "@cs-ts/form-comment";
import { asyncComputed } from "@vueuse/core";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
interface EditingComment extends FormComment {
    visibleToSubmitterEditing: boolean;
}
interface Props {
    id: string;
    hideHistory: boolean;
    comment: EditingComment;
}
export default defineComponent({
    props: ["id", "comment", "hideHistory"],
    emits: ["delete-comment"],
    components: {
        HelpIcon,
    },
    setup(_props: Props, { emit }: SetupContext) {
        const mainStore = useMainStore();
        const pageStore = usePageStore();

        const visibleToSubmitterEditing = ref(false);

        const allowDeleteComments = asyncComputed(() => mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.DeleteCommentsAttachments.Id));
        const adminReview = computed(() => pageStore.adminReview);
        const canDeleteComments = computed(() => adminReview.value && allowDeleteComments.value && pageStore.canDeleteReviewComments);

        const toggleVisibleToSubmitterEditing = (_comment: EditingComment) => {
            visibleToSubmitterEditing.value = !visibleToSubmitterEditing.value;
        };
        const toggleCommentVisibility = async (_comment: EditingComment, val: boolean) => {
            visibleToSubmitterEditing.value = false;
            emit("set-comment-visibility", val);
        };

        return {
            toggleVisibleToSubmitterEditing,
            toggleCommentVisibility,
            visibleToSubmitterEditing,
            adminReview,
            canDeleteComments,
        };
    },
});
</script>
